import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Typography, Button, CircularProgress, Chip, alpha } from '@mui/material';
import {
  SwapHoriz,
  RequestQuoteOutlined,
  ShieldOutlined,
  TollOutlined,
  AddCardOutlined,
  ChevronRight,
  Add,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { CategoryIconBadge } from '../../utils/categoryIcons';
import { formatRupees } from '../../utils/insurance';
import SectionTitle, { dashboardCardSx } from './SectionTitle';

const SHOW = 5;

// Local YYYY-MM-DD → "Today" / "Yesterday" / DD-MM-YYYY (same labels as Transactions)
const dayLabel = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((today - date) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${String(d).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`;
};

const KIND = {
  transaction: { label: 'Transaction', color: 'default' },
  transfer: { label: 'Transfer', color: 'info' },
  loan: { label: 'Loan payment', color: 'warning' },
  insurance: { label: 'Insurance premium', color: 'success' },
  fastag: { label: 'FASTag', color: 'secondary' },
};

const IconCircle = ({ color, children }) => (
  <Box sx={{
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
    bgcolor: (t) => alpha(t.palette[color].main, 0.12), color: `${color}.main`, '& svg': { fontSize: 19 },
  }}>
    {children}
  </Box>
);

// One list of recent money movements from every module, newest first
const loadActivity = async (supabase, userId) => {
  const settle = (promise) => promise.then(({ data, error }) => {
    // A module whose table doesn't exist yet (e.g. Insurance before its SQL is run) is skipped
    if (error) console.error('Recent activity:', error.message);
    return data || [];
  });

  const [transactions, loanPayments, premiums, fastagEntries] = await Promise.all([
    settle(supabase
      .from('transactions')
      .select('id, type, amount, description, date, created_at, transfer_id, wallet_id, wallet:wallets (name), category:categories (*)')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(SHOW * 3)), // transfers come as two rows
    settle(supabase
      .from('loan_payments')
      .select('id, loan_id, amount, payment_date, description, payment_type, created_at')
      .eq('user_id', userId)
      .order('payment_date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(SHOW)),
    settle(supabase
      .from('insurance_premiums')
      .select('id, policy_id, amount, date, notes, created_at, policy:insurance_policies (policy_name, insurer)')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(SHOW)),
    settle(supabase
      .from('fastag_entries')
      .select('id, fastag_id, entry_type, amount, date, toll_plaza, created_at, fastag:fastags (vehicle_number)')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(SHOW)),
  ]);

  // Loan names (loan_payments only stores loan_id)
  let loanNames = {};
  const loanIds = [...new Set(loanPayments.map(p => p.loan_id))];
  if (loanIds.length) {
    const loans = await settle(supabase.from('loans').select('id, name').in('id', loanIds).eq('user_id', userId));
    loanNames = Object.fromEntries(loans.map(l => [l.id, l.name]));
  }

  const items = [];

  // Transfers are stored as two rows sharing transfer_id: show one item, From → To
  const transferLegs = {};
  transactions.forEach(t => {
    if (t.type === 'transfer' && t.transfer_id) (transferLegs[t.transfer_id] = transferLegs[t.transfer_id] || []).push(t);
  });
  const seenTransfers = new Set();
  transactions.forEach(t => {
    if (t.type === 'transfer') {
      if (t.transfer_id && seenTransfers.has(t.transfer_id)) return;
      if (t.transfer_id) seenTransfers.add(t.transfer_id);
      const legs = t.transfer_id ? transferLegs[t.transfer_id] : [t];
      const from = legs.find(l => Number(l.amount) < 0) || t;
      const to = legs.find(l => Number(l.amount) > 0);
      items.push({
        key: `t-${t.id}`,
        kind: 'transfer',
        date: t.date,
        createdAt: t.created_at,
        title: t.description || 'Transfer',
        subtitle: [from.wallet?.name, to?.wallet?.name].filter(Boolean).join(' → '),
        amount: Math.abs(Number(t.amount) || 0),
        neutral: true,
        icon: <IconCircle color="info"><SwapHoriz /></IconCircle>,
        to: '/transactions',
      });
      return;
    }
    items.push({
      key: `t-${t.id}`,
      kind: 'transaction',
      date: t.date,
      createdAt: t.created_at,
      title: t.description || t.category?.name || (t.type === 'income' ? 'Income' : 'Expense'),
      subtitle: [t.category?.name, t.wallet?.name].filter(Boolean).join(' · '),
      amount: Number(t.amount) || 0,
      icon: t.category ? <CategoryIconBadge category={t.category} size={34} /> : <IconCircle color={Number(t.amount) >= 0 ? 'success' : 'error'}><SwapHoriz /></IconCircle>,
      to: '/transactions',
    });
  });

  loanPayments.forEach(p => items.push({
    key: `l-${p.id}`,
    kind: 'loan',
    date: p.payment_date,
    createdAt: p.created_at,
    title: loanNames[p.loan_id] || 'Loan',
    subtitle: [p.payment_type === 'advance' ? 'Advance payment' : 'Loan payment', p.description].filter(Boolean).join(' · '),
    amount: -(Number(p.amount) || 0),
    icon: <IconCircle color="warning"><RequestQuoteOutlined /></IconCircle>,
    to: '/loans',
  }));

  premiums.forEach(p => items.push({
    key: `i-${p.id}`,
    kind: 'insurance',
    date: p.date,
    createdAt: p.created_at,
    title: p.policy?.policy_name || 'Insurance premium',
    subtitle: ['Premium', p.policy?.insurer, p.notes].filter(Boolean).join(' · '),
    amount: -(Number(p.amount) || 0),
    icon: <IconCircle color="success"><ShieldOutlined /></IconCircle>,
    to: `/insurance/${p.policy_id}`,
  }));

  fastagEntries.forEach(e => {
    const recharge = e.entry_type === 'recharge';
    items.push({
      key: `f-${e.id}`,
      kind: 'fastag',
      date: e.date,
      createdAt: e.created_at,
      title: recharge ? 'FASTag recharge' : e.toll_plaza || 'Toll',
      subtitle: [recharge ? 'Recharge' : 'Toll', e.fastag?.vehicle_number].filter(Boolean).join(' · '),
      amount: -(Number(e.amount) || 0),
      icon: <IconCircle color="secondary">{recharge ? <AddCardOutlined /> : <TollOutlined />}</IconCircle>,
      to: `/utilities/fastag/${e.fastag_id}`,
    });
  });

  return items
    .sort((a, b) => String(b.date).slice(0, 10).localeCompare(String(a.date).slice(0, 10))
      || String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    .slice(0, SHOW);
};

// Dashboard → Recent Activity: the last 5 entries across transactions, loan payments,
// insurance premiums and FASTag
const RecentActivity = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    loadActivity(supabase, user.id)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch((error) => console.error('Error loading recent activity:', error))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [supabase, user]);

  return (
    <Box>
      <SectionTitle action={items.length > 0 && (
        <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate('/transactions')}>All transactions</Button>
      )}>
        Recent Activity
      </SectionTitle>
      <Card data-tour="dashboard-activity" sx={{ ...dashboardCardSx, '&:hover': {}, p: { xs: 1, sm: 1.5 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={24} /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>No activity yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your latest transactions, loan payments and insurance premiums will show here.
            </Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/transactions')}>Add a Transaction</Button>
          </Box>
        ) : (
          items.map((item, index) => {
            const kind = KIND[item.kind];
            const positive = item.amount > 0;
            return (
              <Box
                key={item.key}
                role="link"
                tabIndex={0}
                aria-label={`${kind.label}: ${item.title}`}
                onClick={() => navigate(item.to)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(item.to); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 1, sm: 1.5 }, py: 1.25, borderRadius: 2, cursor: 'pointer',
                  borderTop: index ? '1px solid' : 'none', borderColor: 'divider',
                  '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' },
                }}
              >
                {item.icon}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>{item.title}</Typography>
                    {item.kind !== 'transaction' && (
                      <Chip size="small" label={kind.label} color={kind.color} variant="outlined" sx={{ height: 20, fontSize: '0.6875rem', display: { xs: 'none', sm: 'inline-flex' } }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div" noWrap>
                    {item.subtitle || kind.label}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: item.neutral ? 'text.primary' : positive ? 'success.main' : 'error.main',
                  }}>
                    {item.neutral ? '' : positive ? '+' : '−'}{formatRupees(Math.abs(item.amount), 2)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{dayLabel(item.date)}</Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Card>
    </Box>
  );
};

export default RecentActivity;
